const INVALID_FILE_NAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;
const MAX_ORIGINAL_FILENAME_LENGTH = 200;

function splitBaseAndExtension(name: string): { base: string; ext: string } {
  if (!name || name === '.' || name === '..') {
    return { base: '', ext: '' };
  }
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return { base: name, ext: '' };
  }
  return {
    base: name.slice(0, lastDot),
    ext: name.slice(lastDot + 1)
  };
}

function uniquifyDefaultAttachmentName(filename: string): string {
  return filename === 'attachment' ? `attachment_${Date.now()}` : filename;
}

/**
 * Produces a safe display / metadata filename for attachments (preserves extension when possible).
 * Strips path segments, control characters, and characters that are invalid or problematic for paths and URLs.
 * When the result would be exactly `attachment`, appends `_${Date.now()}` so default names do not collide.
 */
export function sanitizeOriginalAttachmentFileName(rawName: string | null | undefined): string {
  if (rawName === undefined || rawName === null) {
    return uniquifyDefaultAttachmentName('attachment');
  }

  let name = String(rawName)
    .replace(/\u0000/g, '')
    .trim();
  if (!name) {
    return uniquifyDefaultAttachmentName('attachment');
  }

  const basename = name.split(/[/\\]/).pop();
  if (!basename || basename === '.' || basename === '..') {
    return uniquifyDefaultAttachmentName('attachment');
  }

  name = basename;

  const { base, ext } = splitBaseAndExtension(name);
  const sanitizedExt = ext.replace(INVALID_FILE_NAME_CHARS, '').replace(/\s+/g, '');
  const sanitizedBase = base
    .replace(INVALID_FILE_NAME_CHARS, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/_+/g, '_')
    .replace(/^\.+$/, '');

  let result =
    sanitizedExt.length > 0 ? `${sanitizedBase || 'attachment'}.${sanitizedExt}` : sanitizedBase || 'attachment';

  if (result.length > MAX_ORIGINAL_FILENAME_LENGTH) {
    const head = result.slice(0, MAX_ORIGINAL_FILENAME_LENGTH - 1);
    const lastDotInHead = head.lastIndexOf('.');
    if (sanitizedExt.length > 0 && lastDotInHead > 0) {
      result = `${head.slice(0, lastDotInHead)}.${sanitizedExt}`.slice(0, MAX_ORIGINAL_FILENAME_LENGTH);
    } else {
      result = head;
    }
  }

  return uniquifyDefaultAttachmentName(result || 'attachment');
}
